import React, { useCallback, useEffect, useState } from 'react';

import {
  addDeviceAndPersist,
  Device,
  disable2FA,
  getRecoveryStatus,
  isSupported,
  listDevices,
  RecoveryStatus,
  regenerateRecoveryCodes,
  removeDevice,
} from '../../../services/webauthnService';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';

const WebAuthnDevicesPanel: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<RecoveryStatus | null>(null);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listDevices();
      setDevices(data.devices);
      setEnabled(data.enabled);
      if (data.enabled) {
        setRecovery(await getRecoveryStatus());
      } else {
        setRecovery(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur 2FA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRegenerate = async () => {
    if (!window.confirm('Régénérer 10 nouveaux codes ? Les anciens seront immédiatement invalidés.')) return;
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const codes = await regenerateRecoveryCodes();
      setNewCodes(codes);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur 2FA');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await addDeviceAndPersist(navigator.userAgent.slice(0, 80));
      setInfo('Appareil enregistré.');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur 2FA');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (credentialId: string) => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await removeDevice(credentialId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur 2FA');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await disable2FA();
      setInfo('2FA désactivé. Vous pouvez la réactiver à tout moment.');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur 2FA');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Chargement…</div>;
  if (!isSupported()) {
    return (
      <div className="text-sm text-amber-700 dark:text-amber-300">
        Ce navigateur ne supporte pas l'authentification biométrique. Utilisez Chrome, Safari ou Edge récent.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
            Double authentification : {enabled ? 'activée' : 'inactive'}
          </p>
          <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300 mt-1">
            Lors de votre prochaine connexion, vous devrez valider votre identité avec Touch ID, Face ID ou Windows
            Hello.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={handleAdd}
            disabled={busy}
            className="px-4 py-2 text-sm text-white bg-mcdm-green-400 hover:bg-mcdm-green-500 disabled:opacity-50 rounded"
          >
            {busy ? 'Patientez…' : enabled ? 'Ajouter un appareil' : 'Activer'}
          </button>
          {enabled && (
            <button
              type="button"
              onClick={handleDisable}
              disabled={busy}
              className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Désactiver la 2FA
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      {info && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">{info}</div>}

      {devices.length > 0 && (
        <ul className="divide-y border rounded">
          {devices.map((d) => (
            <li key={d.id} className="flex items-center justify-between p-3">
              <div className="min-w-0 pr-3">
                <div className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 truncate">
                  {d.label ?? 'Appareil sans nom'}
                </div>
                <div className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300">
                  Ajouté le {new Date(d.createdAt).toLocaleDateString('fr-FR')}
                  {d.lastUsedAt && ` · dernière utilisation ${new Date(d.lastUsedAt).toLocaleDateString('fr-FR')}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(d.id)}
                disabled={busy}
                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                Supprimer
              </button>
            </li>
          ))}
        </ul>
      )}

      {recovery && (
        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                Codes de récupération
              </h4>
              <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300 mt-1">
                {recovery.total > 0
                  ? `${recovery.total - recovery.used} codes restants sur ${recovery.total}${
                      recovery.generatedAt
                        ? ` · générés le ${new Date(recovery.generatedAt).toLocaleDateString('fr-FR')}`
                        : ''
                    }`
                  : 'Aucun code de récupération généré.'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={busy}
              className="px-3 py-1.5 text-xs border border-mcdm-green-400 text-mcdm-green-500 hover:bg-mcdm-green-100 rounded disabled:opacity-50"
            >
              Régénérer
            </button>
          </div>
        </div>
      )}

      {newCodes && <RecoveryCodesDisplay codes={newCodes} onClose={() => setNewCodes(null)} />}
    </div>
  );
};

export default WebAuthnDevicesPanel;
